/**
 * Global OAuth callback page - handles redirect from OAuth providers.
 *
 * This is the single callback URL for all OAuth providers. It parses the
 * state to determine the organization and profile, exchanges the code for
 * tokens, and redirects to the appropriate page.
 */

import {
  OAUTH_ERROR_RETURN_URL_KEY,
  OAUTH_RETURN_URL_KEY,
  STATE_EXPIRED_MESSAGE,
  STATE_EXPIRED_TOKEN,
  useChannelCallback,
} from "@/lib/queries/channel";
import { trpcClient } from "@/lib/trpc";
import { getUserFriendlyOAuthError } from "@/lib/utils/oauth-errors";
import { OAuthCallbackCard } from "@/components/channels/oauth-callback-card";
import { YouTubeChannelPicker } from "@/components/channels/youtube-channel-picker";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export const Route = createFileRoute("/oauth/callback")({
  validateSearch: searchSchema,
  component: OAuthCallbackPage,
});

/**
 * True for a relative path that stays on this origin (guards stored return
 * URLs). Resolving against the origin also rejects tricks like `/\evil.com`,
 * which browsers treat as a protocol-relative URL.
 */
export function isSafeRelativePath(url: string | null): url is string {
  if (!url || !url.startsWith("/")) return false;
  try {
    return (
      new URL(url, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

interface StatePayload {
  profileId: string;
  organizationId: string;
  platform: string;
}

export function OAuthCallbackPage() {
  const { code, state, error, error_description } = Route.useSearch();
  const navigate = useNavigate();

  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [profileSlug, setProfileSlug] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const callbackMutation = useChannelCallback();
  const hasProcessedRef = useRef(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard against double-submit: auth codes are single-use, so track processed codes
  // in sessionStorage to survive remounts on mobile browsers
  const codeKey = code ? `oauth_processed_${code.slice(0, 16)}` : null;

  type ChannelSelection = {
    channels: Array<{
      platformAccountId: string;
      displayName: string;
      handle?: string;
      avatarUrl?: string;
      profileUrl?: string;
    }>;
    tokenBundle: string;
    organizationId: string;
    profileId: string;
    orgSlugValue: string;
    profileSlugValue: string;
  };

  // YouTube channel picker state — lazy-initialized from sessionStorage so iOS
  // Safari page reloads mid-picker restore the picker without a useEffect setState call.
  const [channelSelection, setChannelSelection] =
    useState<ChannelSelection | null>(() => {
      if (!codeKey) return null;
      const stored = sessionStorage.getItem(`${codeKey}_yt`);
      if (!stored) return null;
      try {
        return JSON.parse(stored) as ChannelSelection;
      } catch {
        return null;
      }
    });

  // Parse state payload - memoized to avoid re-parsing
  const statePayload = useMemo((): StatePayload | null => {
    if (!state) return null;
    try {
      const [payloadBase64] = state.split(".");
      const payloadJson = atob(payloadBase64);
      return JSON.parse(payloadJson) as StatePayload;
    } catch {
      return null;
    }
  }, [state]);

  // Determine error state from URL params
  const hasOAuthError = Boolean(error);
  const hasMissingParams = !code || !state;
  const hasInvalidState = !hasMissingParams && !statePayload;
  const urlErrorMessage = error_description || error || null;

  // Detect double-submit: auth codes are single-use and resubmitting a
  // redeemed code returns invalid_grant. Mobile browsers (especially iOS)
  // can remount components on background/foreground transitions.
  //
  // Guard with !hasProcessedRef.current so the current mount's own
  // sessionStorage.setItem(codeKey, "1") call — which happens synchronously
  // before processCallback() resolves — doesn't flip isAlreadyProcessed to
  // true mid-flight and trigger the returnUrl redirect while channel.callback
  // is still running. Without this guard the picker is never shown for
  // YouTube flows because the component unmounts before setChannelSelection
  // can be called. The org-scoped callback already has this guard.
  const isAlreadyProcessed =
    !hasProcessedRef.current &&
    Boolean(codeKey && sessionStorage.getItem(codeKey));

  // Process the OAuth callback
  useEffect(() => {
    if (hasProcessedRef.current) return;
    if (hasOAuthError || hasMissingParams || hasInvalidState) return;
    if (!code || !state || !statePayload) return;
    if (isAlreadyProcessed) return;

    hasProcessedRef.current = true;
    if (codeKey) sessionStorage.setItem(codeKey, "1");

    const processCallback = async () => {
      try {
        // Fetch org and profile in parallel to get slugs
        const [orgResult, profile] = await Promise.all([
          trpcClient.organization.get.query({
            organizationId: statePayload.organizationId,
          }),
          trpcClient.organization.profiles.get.query({
            organizationId: statePayload.organizationId,
            profileId: statePayload.profileId,
          }),
        ]);

        const orgSlugValue = orgResult.organization?.slug;
        const profileSlugValue = profile?.slug;
        setOrgSlug(orgSlugValue ?? null);
        setProfileSlug(profileSlugValue ?? null);

        // Exchange the code for tokens
        const result = await callbackMutation.mutateAsync({
          organizationId: statePayload.organizationId,
          code,
          state,
        });

        // Slugs are required for both the post-picker redirect and for non-picker flows.
        // Validate before entering either branch so empty strings can never reach the picker.
        if (!orgSlugValue || !profileSlugValue) {
          setParseError("Unable to determine redirect destination");
          return;
        }

        // YouTube returns a channel list for the user to pick from
        if (result.type === "channel_selection_required") {
          const sel = {
            channels: result.channels,
            tokenBundle: result.tokenBundle,
            organizationId: statePayload.organizationId,
            profileId: statePayload.profileId,
            orgSlugValue,
            profileSlugValue,
          };
          // Persist picker state so iOS Safari reloads can restore the picker
          if (codeKey)
            sessionStorage.setItem(`${codeKey}_yt`, JSON.stringify(sel));
          setChannelSelection(sel);
          return;
        }

        // Check for a stored return URL (e.g. from sign-brief page).
        // Key is namespaced by OAuth state to support concurrent flows.
        const returnKey = state
          ? `${OAUTH_RETURN_URL_KEY}:${state}`
          : OAUTH_RETURN_URL_KEY;
        const returnUrl = localStorage.getItem(returnKey);
        localStorage.removeItem(returnKey);

        // Validate returnUrl is a safe relative path to prevent open redirects
        const isSafeReturn = isSafeRelativePath(returnUrl);

        localStorage.removeItem(`${OAUTH_ERROR_RETURN_URL_KEY}:${state}`);

        // Clean up the double-submit guard on success
        if (codeKey) {
          sessionStorage.removeItem(codeKey);
          sessionStorage.removeItem(`${codeKey}_yt`);
        }

        redirectTimeoutRef.current = setTimeout(() => {
          if (isSafeReturn) {
            navigate({ href: returnUrl });
          } else {
            navigate({
              to: "/o/$orgSlug/profiles/$profileSlug",
              params: { orgSlug: orgSlugValue, profileSlug: profileSlugValue },
              search: { tab: "channels" },
            });
          }
        }, 2000);
      } catch (err) {
        // Clean up so user can retry
        if (codeKey) {
          sessionStorage.removeItem(codeKey);
          sessionStorage.removeItem(`${codeKey}_yt`);
        }
        const rawMessage =
          (err as Error).message || "Failed to connect account";
        setParseError(getUserFriendlyOAuthError(rawMessage));
      }
    };

    processCallback();

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [
    code,
    state,
    statePayload,
    hasOAuthError,
    hasMissingParams,
    hasInvalidState,
    navigate,
    codeKey,
  ]);

  // iOS Safari reloads the callback page mid-flight, setting isAlreadyProcessed = true
  // before the API call completes. If the YouTube picker was active, the lazy useState
  // initializer already restored it from sessionStorage. For non-picker flows, redirect
  // to the stored returnUrl if one exists from the previous successful attempt.
  useEffect(() => {
    if (!isAlreadyProcessed) return;
    if (channelSelection) return; // picker already restored — nothing to do
    const returnKey = state
      ? `${OAUTH_RETURN_URL_KEY}:${state}`
      : OAUTH_RETURN_URL_KEY;
    const returnUrl = localStorage.getItem(returnKey);
    const isSafe = isSafeRelativePath(returnUrl);
    if (!isSafe) return;
    localStorage.removeItem(returnKey);
    navigate({ href: returnUrl });
  }, [isAlreadyProcessed, channelSelection, state, navigate]);

  // Detect state expiry errors for user-friendly messaging
  const isStateExpired =
    parseError?.toLowerCase().includes(STATE_EXPIRED_TOKEN) ||
    (callbackMutation.error instanceof Error &&
      callbackMutation.error.message
        .toLowerCase()
        .includes(STATE_EXPIRED_TOKEN));

  // Derive display status — channel_selection_required is not a final success
  const isConnected =
    callbackMutation.isSuccess && callbackMutation.data?.type === "connected";

  const status =
    hasOAuthError ||
    hasMissingParams ||
    hasInvalidState ||
    callbackMutation.isError ||
    parseError
      ? "error"
      : isConnected
        ? "success"
        : "processing";

  const errorMessage = isStateExpired
    ? STATE_EXPIRED_MESSAGE
    : hasOAuthError
      ? getUserFriendlyOAuthError(
          urlErrorMessage || "Failed to connect account",
        )
      : hasMissingParams
        ? "Something went wrong — we didn't receive the expected information from the social platform. Please go back and try again."
        : hasInvalidState
          ? "Something went wrong with your session. Please go back and try connecting your account again."
          : parseError
            ? parseError
            : callbackMutation.isError
              ? getUserFriendlyOAuthError(
                  (callbackMutation.error as Error)?.message ||
                    "Failed to connect account",
                )
              : null;

  // A flow that registered an error return URL (e.g. the partner connect page)
  // handles failures itself, so it can offer a retry in context.
  useEffect(() => {
    if (status !== "error" || !state || !errorMessage) return;
    const errorKey = `${OAUTH_ERROR_RETURN_URL_KEY}:${state}`;
    const errorReturnUrl = localStorage.getItem(errorKey);
    if (!isSafeRelativePath(errorReturnUrl)) return;
    localStorage.removeItem(errorKey);
    localStorage.removeItem(`${OAUTH_RETURN_URL_KEY}:${state}`);
    const separator = errorReturnUrl.includes("?") ? "&" : "?";
    navigate({
      href: `${errorReturnUrl}${separator}message=${encodeURIComponent(errorMessage)}`,
    });
  }, [status, state, errorMessage, navigate]);

  // Back navigation is dynamic — depends on whether we resolved org/profile slugs
  const backTo =
    orgSlug && profileSlug
      ? "/o/$orgSlug/profiles/$profileSlug"
      : orgSlug
        ? "/o/$orgSlug/profiles"
        : "/";

  const backParams: Record<string, string> | undefined =
    orgSlug && profileSlug
      ? { orgSlug, profileSlug }
      : orgSlug
        ? { orgSlug }
        : undefined;

  const backSearch = orgSlug && profileSlug ? { tab: "channels" } : undefined;

  const backLabel =
    orgSlug && profileSlug
      ? isStateExpired
        ? "Try again"
        : "Go back to social accounts"
      : orgSlug
        ? "Go back to profiles"
        : "Go to home";

  // Show YouTube channel picker when channels are ready to select
  if (channelSelection) {
    return (
      <YouTubeChannelPicker
        channels={channelSelection.channels}
        tokenBundle={channelSelection.tokenBundle}
        organizationId={channelSelection.organizationId}
        profileId={channelSelection.profileId}
        onSuccess={() => {
          if (codeKey) {
            sessionStorage.removeItem(codeKey);
            sessionStorage.removeItem(`${codeKey}_yt`);
          }
          const returnKey = state
            ? `${OAUTH_RETURN_URL_KEY}:${state}`
            : OAUTH_RETURN_URL_KEY;
          const returnUrl = localStorage.getItem(returnKey);
          localStorage.removeItem(returnKey);
          localStorage.removeItem(`${OAUTH_ERROR_RETURN_URL_KEY}:${state}`);
          const isSafe = isSafeRelativePath(returnUrl);
          // Clear picker immediately so re-renders before navigation don't re-show it
          const { orgSlugValue, profileSlugValue } = channelSelection;
          setChannelSelection(null);
          if (isSafe) {
            navigate({ href: returnUrl });
          } else {
            navigate({
              to: "/o/$orgSlug/profiles/$profileSlug",
              params: {
                orgSlug: orgSlugValue,
                profileSlug: profileSlugValue,
              },
              search: { tab: "channels" },
            });
          }
        }}
        onError={(msg) => {
          if (codeKey) sessionStorage.removeItem(`${codeKey}_yt`);
          setChannelSelection(null);
          setParseError(getUserFriendlyOAuthError(msg));
        }}
      />
    );
  }

  return (
    <OAuthCallbackCard
      status={status}
      errorMessage={errorMessage}
      isStateExpired={isStateExpired}
      backLabel={backLabel}
      backTo={backTo}
      backParams={backParams}
      backSearch={backSearch}
    />
  );
}
